"use client";

// packages/eden-form-parts/src/components/InputGroup.jsx
import { forwardRef, useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import {
  sharedInputCss,
  formElementDisabledBackgroundDef,
  formElementDisabledBackground,
  formElementDisabledBorderDef,
  formElementDisabledBorder
} from '../utils/sharedCss.js';
import { InternalUseOnlyInput } from './Input.js';
import {
  backgroundColorLevelCurrent,
  spacing4,
  borderRadius
} from "@churchofjesuschrist/eden-style-constants";
import {
  ValidityStateContext,
  SmallContext,
  DisabledContext
} from '../utils/context.js';
import { jsx } from "react/jsx-runtime";
var InternalUseOnlyInputGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: ${borderRadius};
  background-color: ${backgroundColorLevelCurrent};
  ${formElementDisabledBackgroundDef}
  ${formElementDisabledBorderDef}
  ${({ $disabled }) => $disabled ? `background:${formElementDisabledBackground()}; && ${InternalUseOnlyInput} { background: transparent; }` : ""}

  &::after {
    content: "";
    ${sharedInputCss};
    ${({ $disabled }) => $disabled ? `border-color: ${formElementDisabledBorder()};` : ""}
    position: absolute;
    pointer-events: none;
    background: none;
    height: auto;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  &:has(${InternalUseOnlyInput}:focus) {
    &::after {
      outline: highlight auto 1px; /* highlight is for firefox support */
      outline: -webkit-focus-ring-color auto 1px; /* -webkit-focus-ring-color works in chrome, safari, edge */
      outline-offset: 2px;
    }

    & ${InternalUseOnlyInput} {
      outline: none;
    }
  }

  & > ${InternalUseOnlyInput}, & > ${InternalUseOnlyInput}:focus, & > &::after,
  & div ::after {
    border: 0;
  }

  & > button {
    margin: ${({ $small }) => $small ? "0" : spacing4};
  }

  & > &,
  & > ${InternalUseOnlyInput} {
    margin: 0;
  }
`;
var InputGroupComponent = ({ children, small, disabled, ...props }, forwardedRef) => {
  const internalRef = useRef();
  const ref = forwardedRef ?? internalRef;
  const validityStateContext = useContext(ValidityStateContext);
  useEffect(() => {
    const buttons = ref.current.querySelectorAll("button");
    buttons.forEach((button) => button.disabled = disabled);
  }, [disabled, ref]);
  return /* @__PURE__ */ jsx(
    InternalUseOnlyInputGroup,
    {
      $validityState: validityStateContext,
      $small: small,
      $disabled: disabled,
      ...props,
      ref,
      children: /* @__PURE__ */ jsx(SmallContext.Provider, { value: small, children: /* @__PURE__ */ jsx(DisabledContext.Provider, { value: disabled, children }) })
    }
  );
};
var InputGroup = forwardRef(InputGroupComponent);
InputGroup.displayName = "InputGroup";
var InputGroup_default = InputGroup;
export {
  InternalUseOnlyInputGroup,
  InputGroup_default as default
};
